import './LabelingRulesModal.scss';
import * as R from 'ramda';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdSmsFailed } from 'react-icons/md';
import {
  isBillingSegmentName,
  MAX_FILTERABLE_DEFAULT_SEGMENT_ID,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import {
  makeFilter,
  Option,
  parseTokensBasicToAdvanced
} from '@blisspointmedia/bpm-types/dist/FilterBar';
import { Button } from '@Components/Button';
import { AlertDialog, AlertDialogAction } from '@Components/AlertDialog';
import { Dropdown } from '@Components/Dropdown';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';
import { FilterPaneAdvanced } from '@Components/FetchFilterBar/FilterPaneAdvanced';
import {
  Appearance,
  Design,
  Intent,
  IntentType,
  Placement,
  Size
} from '@Components/designConstants';
import { Warning500 } from '../../utils/colors';
import { DateRange } from '../../utils/date-utils';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';
import {
  filterStringToOptions,
  filterTokensToString,
  getBasicFilterBarOptions,
  getCategories,
  getFilterBarOptions
} from './segmentationMappingUtils';

type FilterMode = 'Basic' | 'Advanced';

interface AlertModalSettings {
  title: string;
  body: string;
  icon: ReactNode;
  intent: IntentType;
  primaryAction: AlertDialogAction;
}

interface LabelingRulesModalProps {
  rule?: SegmentationMappingLabelRule | BillingMappingLabelRule | undefined;
  customSegments: CustomSegmentsData[] | undefined;
  isNew: boolean;
  show: boolean;
  setShow: (show: boolean) => void;
  onRuleSave: (rule: SegmentationMappingLabelRule | BillingMappingLabelRule) => void;
  rules?: SegmentationMappingLabelRule[];
  dataGranularity?: 'ad' | 'ad_group' | 'campaign';
  data: unknown[];
  activeTab?: string;
}

export const LabelingRulesModal = ({
  customSegments,
  isNew,
  show,
  setShow,
  rule,
  onRuleSave,
  dataGranularity = 'campaign',
  rules = [],
  data,
  activeTab
}: LabelingRulesModalProps) => {
  const [advanced, setAdvanced] = useState<boolean>(true);
  const [segmentName, setSegmentName] = useState<string>('');
  const [segmentId, setSegment] = useState<number>(0);
  const [advancedFilter, setAdvancedFilter] = useState<string>('');
  const [basicFilter, setBasicFilter] = useState<string>('');
  const [segmentValueId, setSegmentValue] = useState<number>(0);
  const [segmentValueName, setSegmentValueName] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<Partial<DateRange>>({
    start: '',
    end: ''
  });
  const setEditing = useState(false)[1];
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isBillingSegment, setIsBillingSegment] = useState<boolean>(false);

  useEffect(() => {
    setIsBillingSegment(isBillingSegmentName(segmentName));
  }, [segmentName]);

  const [alertModalSettings, setAlertModalSettings] = useState<AlertModalSettings>({
    title: '',
    body: '',
    icon: undefined,
    intent: Intent.INFO,
    primaryAction: {}
  });

  const isSegmentLocked = useMemo(() => {
    if (!activeTab || activeTab === 'all') {
      return false;
    }
    return customSegments?.some(segment => segment.segmentName === activeTab);
  }, [activeTab, customSegments]);

  useEffect(() => {
    if (!show || !customSegments || activeTab === 'all' || !activeTab) {
      return;
    }

    const matchedSegment = customSegments.find(segment => segment.segmentName === activeTab);

    if (matchedSegment) {
      setSegment(Number(matchedSegment.segmentId));
      setSegmentName(matchedSegment.segmentName);
    } else {
      setSegment(0);
      setSegmentName('');
    }
    setSegmentValue(0);
    setSegmentValueName('');
  }, [activeTab, show, customSegments]);

  const filterBarOptions = useMemo(() => {
    // custom segments for the filter bar only include Channel and Platform (IDs 1 and 2), the rest are not needed for the filter bar
    const segments = customSegments?.filter(
      segment =>
        segment.segmentId !== null && Number(segment.segmentId) <= MAX_FILTERABLE_DEFAULT_SEGMENT_ID
    );
    return getFilterBarOptions(segments, dataGranularity);
  }, [customSegments, dataGranularity]);

  const basicFilterBarOptions = useMemo(
    () => getBasicFilterBarOptions(filterBarOptions),
    [filterBarOptions]
  );

  const categories = useMemo(
    () => getCategories(filterBarOptions, data as never[], customSegments || []),
    [customSegments, data, filterBarOptions]
  );

  const baseRule = useMemo(() => {
    if (rule) {
      return rule;
    }
    return {
      filterTokens: [],
      setSegmentId: 0,
      setSegmentValueId: 0,
      isAdvanced: true,
      isActive: true
    };
  }, [rule]);

  const baseBillingRule = useMemo(() => {
    if (rule) {
      return rule;
    }
    return {
      filterTokens: [],
      setBillingSegment: '',
      setBillingSegmentValue: '',
      startDate: '',
      endDate: null,
      isAdvanced: true,
      isActive: true
    } as unknown as Partial<BillingMappingLabelRule>;
  }, [rule]);

  useEffect(() => {
    if (rule && show) {
      setAdvanced(rule.isAdvanced);
      if ('setSegmentId' in rule) {
        setSegment(rule.setSegmentId);
        setSegmentName(
          customSegments?.find(segment => Number(segment.segmentId) === rule.setSegmentId)
            ?.segmentName || ''
        );
        setSegmentValue(rule.setSegmentValueId);
        setSegmentValueName(
          customSegments
            ?.find(segment => Number(segment.segmentId) === rule.setSegmentId)
            ?.values.find(value => Number(value.valueId) === rule.setSegmentValueId)?.valueName ||
            ''
        );
      } else {
        setSegmentName(rule.setBillingSegment);
        setSegmentValueName(rule.setBillingSegmentValue);
        setSelectedDateRange(
          rule.startDate
            ? {
                start: rule.startDate,
                end: rule.endDate ? rule.endDate : ''
              }
            : {
                start: '',
                end: ''
              }
        );
        setIsBillingSegment(true);
      }

      if (rule.isAdvanced) {
        setAdvancedFilter(
          filterTokensToString(rule.filterTokens as unknown as Option[], categories, true)
        );
      } else {
        setBasicFilter(
          filterTokensToString(rule.filterTokens as unknown as Option[], categories, false)
        );
      }
    }
  }, [rule, show, customSegments, categories]);

  const granularityHeader = {
    ad: 'Campaign, Ad Group, or Ad includes:',
    ad_group: 'Campaign or Ad Group includes:',
    campaign: 'Campaign includes:'
  }[dataGranularity];

  const segmentOptions = useMemo(() => {
    const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
    return customSegments
      ?.filter(segment => !systemNames.includes(segment.segmentName))
      .map(segment => ({
        label: segment.segmentName,
        value: segment.segmentId,
        segmentValues: segment.values.map(value => ({
          label: value.valueName,
          value: value.valueId
        }))
      }));
  }, [customSegments]);

  const segmentValueOptions = useMemo(() => {
    if (segmentId === 0 && !isBillingSegment) {
      return [];
    }
    if (isBillingSegment) {
      return segmentOptions?.find(option => option.label === segmentName)?.segmentValues;
    }
    return segmentOptions?.find(option => Number(option.value) === segmentId)?.segmentValues;
  }, [segmentId, segmentOptions, segmentName, isBillingSegment]);

  const filterTokens = useCallback(() => {
    if (advanced) {
      return filterStringToOptions(advancedFilter, filterBarOptions, data as never[]);
    }
    return [{ label: basicFilter, value: basicFilter }];
  }, [advanced, advancedFilter, basicFilter, data, filterBarOptions]);

  const validateFilter = useMemo(() => {
    try {
      let tokens = filterTokens();
      if (!advanced) {
        tokens = parseTokensBasicToAdvanced(tokens, basicFilterBarOptions);
      }
      // filterBarOptions is a superset of basicFilterBarOptions, so it covers both cases here.
      makeFilter(tokens, filterBarOptions);
      return '';
    } catch (e) {
      if (e.message.includes("You can't end with an operation")) {
        return 'An invalid keyword or no keyword was entered.';
      }
      return e.message;
    }
  }, [advanced, filterTokens, filterBarOptions, basicFilterBarOptions]);
  const sameSegmentRules = useMemo(
    () => rules.filter(r => r.setSegmentId === Number(segmentId)),
    [rules, segmentId]
  );

  const isDuplicateRule = () => {
    const currentFilterTokens = filterTokens();
    return sameSegmentRules.some(
      r =>
        R.equals(r.filterTokens as unknown, currentFilterTokens as unknown) &&
        (r.id !== rule?.id || (r.id === undefined && r.createdAt !== rule?.createdAt))
    );
  };

  const missingFields = useMemo(() => {
    const filter = filterTokens();
    return [
      (!filter || !filter.length || (!basicFilter && !advanced)) && 'A valid filter must be set',
      ((!segmentId && !isBillingSegment) || (isBillingSegment && segmentName.length === 0)) &&
        'A segment must be selected',
      ((!segmentValueId && !isBillingSegment) ||
        (isBillingSegment && segmentValueName.length === 0)) &&
        'A segment value must be selected',
      isBillingSegment && !selectedDateRange?.start && 'A start date must be selected'
    ].filter(Boolean) as string[];
  }, [
    advanced,
    basicFilter,
    filterTokens,
    segmentId,
    segmentValueId,
    isBillingSegment,
    segmentName,
    segmentValueName,
    selectedDateRange
  ]);

  const segmentValueMismatch = useMemo(() => {
    const segment = customSegments?.find(s => Number(s.segmentId) === segmentId);
    return !segment?.values.some(value => value.valueId === segmentValueId);
  }, [customSegments, segmentId, segmentValueId]);

  const resetRule = () => {
    setBasicFilter('');
    setAdvancedFilter('');
    setSegmentName('');
    setSegment(0);
    setSegmentValue(0);
    setSegmentValueName('');
    setAdvanced(true);
    setSelectedDateRange({
      start: '',
      end: ''
    });
    setIsBillingSegment(false);
  };

  const handleCancel = () => {
    resetRule();
    setShow(false);
  };

  const modifiedRule = useCallback(
    () => ({
      ...baseRule,
      filterTokens: filterTokens(),
      setSegmentId: Number(segmentId),
      setSegmentValueId: Number(segmentValueId),
      isAdvanced: advanced
    }),
    [baseRule, filterTokens, segmentId, segmentValueId, advanced]
  );

  const modifiedBillingRule = useCallback(
    () => ({
      ...baseBillingRule,
      filterTokens: filterTokens(),
      setBillingSegment: segmentName,
      setBillingSegmentValue: segmentValueName,
      startDate: selectedDateRange?.start,
      endDate: selectedDateRange?.end || null,
      isAdvanced: advanced
    }),
    [baseBillingRule, filterTokens, segmentName, segmentValueName, selectedDateRange, advanced]
  );

  const handleSave = () => {
    const commonModalSettings = {
      icon: <MdSmsFailed color={Warning500} size={75} />,
      intent: Intent.ERROR,
      primaryAction: { onClick: () => setShowAlertModal(false), label: 'Back' }
    };

    if (validateFilter) {
      setAlertModalSettings({
        ...commonModalSettings,
        title: 'Invalid Filter',
        body: validateFilter
      });
      setShowAlertModal(true);
      setIsSaving(false);
      return;
    }

    if (missingFields.length > 0) {
      setAlertModalSettings({
        ...commonModalSettings,
        title: 'Missing Fields',
        body: missingFields.join('. ')
      });
      setShowAlertModal(true);
      setIsSaving(false);
      return;
    }

    if (segmentId && segmentValueId && segmentValueMismatch) {
      setAlertModalSettings({
        ...commonModalSettings,
        title: 'Segment Value Mismatch',
        body: 'The selected segment value does not match the selected segment, please change the segment value or segment.'
      });
      setShowAlertModal(true);
      setIsSaving(false);
      return;
    }

    setIsSaving(true);
    setTimeout(() => {
      if (isDuplicateRule()) {
        setAlertModalSettings({
          ...commonModalSettings,
          title: 'Duplicate Rule',
          body: 'A rule already exists with this filter and selected segment, please change the filter or selected segment.'
        });
        setShowAlertModal(true);
        setIsSaving(false);
        return;
      }

      const ruleToSave = isBillingSegment ? modifiedBillingRule() : modifiedRule();

      onRuleSave(ruleToSave as unknown as SegmentationMappingLabelRule | BillingMappingLabelRule);

      resetRule();
      setShow(false);
      setIsSaving(false);
    }, 0);
  };

  const handleToggleChange = (option: FilterMode) => {
    const toggledAdvanced = option === 'Advanced';
    if (toggledAdvanced) {
      setBasicFilter('');
    } else {
      setAdvancedFilter('');
    }
    setAdvanced(toggledAdvanced);
  };

  return (
    <>
      <AlertDialog
        title={alertModalSettings.title}
        body={alertModalSettings.body}
        icon={alertModalSettings.icon}
        intent={alertModalSettings.intent}
        primaryAction={alertModalSettings.primaryAction}
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
      />
      <Modal show={show} onHide={handleCancel} className='labeling-rules-modal' size='lg'>
        <Modal.Header>
          <div className='title-container'>
            <Modal.Title>
              {isNew ? 'New' : 'Edit'} Labeling Rule {!isNew && rule ? rule.name : ''}
            </Modal.Title>
          </div>

          <SegmentedControlButton<FilterMode>
            size={Size.LG}
            options={['Basic', 'Advanced']}
            selectedOption={advanced ? 'Advanced' : 'Basic'}
            onChange={handleToggleChange}
          />
        </Modal.Header>
        <Modal.Body>
          <div className='filter-select'>
            {advanced ? (
              <div className='advanced-filter-pane'>
                <div className='label'>If these conditions exist:</div>
                <FilterPaneAdvanced
                  categories={categories}
                  compileError=''
                  setEditing={setEditing}
                  setState={setAdvancedFilter}
                  state={advancedFilter}
                  showAdvancedHeader={false}
                />
              </div>
            ) : (
              <div className='basic-filter-pane'>
                <div className='label'>{granularityHeader}</div>
                <Form.Control
                  type='text'
                  placeholder='e.g. _Brand_'
                  value={basicFilter}
                  onChange={e => {
                    setBasicFilter(e.currentTarget.value);
                  }}
                />
              </div>
            )}
          </div>
          <div className='segment-selection-container'>
            <div className='dropdown segment-select'>
              {isSegmentLocked ? (
                <div className='label'>{`Then set ${segmentName}`}</div>
              ) : (
                <>
                  <div className='label'>Then set this segment:</div>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    className='segment-dropdown'
                    hasFilterBar={segmentOptions && segmentOptions.length > 6}
                    placeholder='Select Segment'
                    options={segmentOptions?.map(op => op.label) || []}
                    value={segmentName}
                    onChange={value => {
                      setSegmentName(value);
                      const id = segmentOptions?.find(op => op.label === value)?.value;
                      setSegment(Number(id));
                      setSegmentValue(0);
                      setSegmentValueName('');
                    }}
                  />
                </>
              )}
            </div>
            <div className='dropdown'>
              <div className='label'>To this value:</div>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                className='segment-dropdown'
                hasFilterBar={segmentValueOptions && segmentValueOptions.length > 6}
                placeholder='Select Segment Value'
                options={segmentValueOptions?.map(op => op.label) || []}
                value={segmentValueName}
                onChange={v => {
                  setSegmentValueName(v);
                  const id = segmentValueOptions?.find(op => op.label === v)?.value;
                  setSegmentValue(Number(id));
                }}
                disabled={!(segmentId || segmentName)}
              />
            </div>
            {isBillingSegment && (
              <div className='date-range-container'>
                <div className='label'>During these dates:</div>
                <div className='date-range-picker-container'>
                  <div className='start-date-picker'>
                    <DateInputField
                      label='Start Date (required)'
                      value={selectedDateRange?.start || ''}
                      invalidateDateOrRange={() => false}
                      setValue={(date: string) => {
                        const end =
                          !selectedDateRange?.end || date > selectedDateRange.end
                            ? ''
                            : selectedDateRange.end;
                        setSelectedDateRange({
                          ...selectedDateRange,
                          start: date,
                          end
                        });
                      }}
                    />
                  </div>
                  <div className='end-date-picker'>
                    <DateInputField
                      label='End Date (optional)'
                      value={selectedDateRange?.end || ''}
                      invalidateDateOrRange={() => false}
                      setValue={(date: string) => {
                        const end =
                          !date || (selectedDateRange?.start && date < selectedDateRange.start)
                            ? ''
                            : date;
                        setSelectedDateRange({
                          ...selectedDateRange,
                          end
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className='cancel-button'
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button className='save-button' loading={isSaving} onClick={handleSave}>
            {isNew ? 'Add Rule' : 'Update Rule'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
